"use client";

import { Heart } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface SaveSchoolButtonViewProps {
  saved: boolean;
  loading: boolean;
  onClick?: () => void;
  testId?: string;
  disabled?: boolean;
}

function getTooltipLabel(saved: boolean): string {
  return saved ? "Remove from saved" : "Save school";
}

function HeartIcon({ saved }: { saved: boolean }) {
  return (
    <Heart
      className={cn(
        "transition-transform duration-200",
        saved && "fill-current scale-110",
      )}
    />
  );
}

export function SaveSchoolButtonWithTextView({
  saved,
  loading,
  onClick,
  testId,
  disabled,
}: SaveSchoolButtonViewProps) {
  const tooltipLabel = getTooltipLabel(saved);
  const buttonText = saved ? "Saved" : "Save school";

  return (
    <Button
      variant="outline"
      size="lg"
      className={cn(
        "w-full h-11 text-sm",
        saved &&
          "border-primary/30 bg-primary/10 text-primary hover:bg-primary/15",
      )}
      onClick={onClick}
      disabled={loading || disabled}
      data-testid={testId}
      aria-label={tooltipLabel}
    >
      <HeartIcon saved={saved} />
      {buttonText}
    </Button>
  );
}

export default function SaveSchoolButtonView({
  saved,
  loading,
  onClick,
  testId,
  disabled,
}: SaveSchoolButtonViewProps) {
  const tooltipLabel = getTooltipLabel(saved);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "rounded-md h-10 w-10 p-0",
              saved &&
                "border-primary/30 bg-primary/10 text-primary hover:bg-primary/15",
            )}
            onClick={onClick}
            disabled={loading || disabled}
            data-testid={testId}
            aria-label={tooltipLabel}
          >
            <HeartIcon saved={saved} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipLabel}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
